import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, Inject, Input, OnDestroy } from '@angular/core';
import { AgrPrintPageModule } from '@next/core-lib/components';

import { PrintableReport } from './report-print.model';
import { ReportPrintService } from './report-print.service';

@Component({
  selector: 'app-report-print',
  templateUrl: './report-print.component.html',
  styleUrls: ['./report-print.component.scss'],
  imports: [AgrPrintPageModule],
})
export class ReportPrintComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) public report!: PrintableReport;

  private readonly afterPrint = () => this.reportPrintService.close();
  private printTimeoutId?: ReturnType<typeof setTimeout>;
  private originalDocumentTitle?: string;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly reportPrintService: ReportPrintService,
  ) {}

  ngAfterViewInit(): void {
    this.originalDocumentTitle = this.document.title;
    this.document.title = this.report.fileName;
    this.document.body.classList.add('dashboard-report-printing');

    const resourcesReady = this.waitForPrintResources();
    const fallback = new Promise<void>((resolve) => {
      this.printTimeoutId = globalThis.setTimeout(resolve, 500);
    });

    Promise.race([resourcesReady, fallback])
      .catch(() => undefined)
      .then(() => {
        this.printTimeoutId = globalThis.setTimeout(() => {
          this.printTimeoutId = undefined;
          globalThis.print();
        });
      });

    globalThis.addEventListener('afterprint', this.afterPrint);
  }

  ngOnDestroy(): void {
    if (this.printTimeoutId !== undefined) {
      globalThis.clearTimeout(this.printTimeoutId);
    }
    globalThis.removeEventListener('afterprint', this.afterPrint);
    this.document.body.classList.remove('dashboard-report-printing');
    if (this.originalDocumentTitle !== undefined) {
      this.document.title = this.originalDocumentTitle;
    }
  }

  private waitForPrintResources(): Promise<void> {
    const readiness: Promise<unknown>[] = [];

    const image = this.document.querySelector(
      '.dashboard-report-print__logo',
    ) as HTMLImageElement | null;
    if (image) {
      readiness.push(this.waitForImage(image));
    }

    const fonts = this.document.fonts;
    if (fonts) {
      readiness.push(fonts.ready.catch(() => undefined));
    }

    return Promise.all(readiness).then(() => undefined);
  }

  private waitForImage(image: HTMLImageElement): Promise<void> {
    if (image.decode) {
      return image.decode().catch(() => undefined);
    }

    return new Promise<void>((resolve) => {
      if (image.complete) {
        resolve();
        return;
      }

      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener('error', () => resolve(), { once: true });
    });
  }
}
